/**
 * FERP Portal - Focus Context
 *
 * DRR-178 Phase 3: Persistent "I'm working on actor X" state.
 *
 * Backed by Valkey (8-hour TTL, shift-aligned). The focus context scopes
 * all primitive list views to the focused subject. CRITICAL safety events
 * break through the focus filter (DRR-178 D4).
 *
 * Two perspectives:
 * - "subject": Show me all work FOR this actor (frontline staff)
 * - "performer": Show me all work BY this actor (supervisor)
 *
 * Usage:
 * ```tsx
 * const { focusedSubject, setFocus, clearFocus, isFocused } = useFocusContext();
 * ```
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import {
  useGetFocusContextQuery,
  useSetFocusContextMutation,
  useClearFocusContextMutation,
} from '@/generated/graphql';

// ============================================================================
// Types
// ============================================================================

interface FocusState {
  subjectActorId: string | null;
  subjectDisplayName: string | null;
  perspective: 'subject' | 'performer';
}

interface FocusContextValue {
  /** The currently focused actor ID (null if no focus) */
  focusedSubject: string | null;
  /** Display name of focused actor */
  focusedDisplayName: string | null;
  /** Focus perspective: "subject" or "performer" */
  perspective: 'subject' | 'performer';
  /** Whether a focus is active */
  isFocused: boolean;
  /** Set focus on an actor */
  setFocus: (actorId: string, displayName?: string, perspective?: 'subject' | 'performer') => void;
  /** Clear focus — return to unscoped view */
  clearFocus: () => void;
  /** Loading state */
  loading: boolean;
}

const FocusCtx = createContext<FocusContextValue>({
  focusedSubject: null,
  focusedDisplayName: null,
  perspective: 'subject',
  isFocused: false,
  setFocus: () => {},
  clearFocus: () => {},
  loading: false,
});

// ============================================================================
// Provider
// ============================================================================

export function FocusContextProvider({ children }: { children: React.ReactNode }) {
  // Optimistic local state for instant UI feedback
  const [localFocus, setLocalFocus] = useState<FocusState>({
    subjectActorId: null,
    subjectDisplayName: null,
    perspective: 'subject',
  });

  // Backend query (hydrates on mount from Valkey)
  const { data: focusData, loading: queryLoading } = useGetFocusContextQuery({
    fetchPolicy: 'network-only',
  });

  // Hydrate local state from Valkey response
  React.useEffect(() => {
    const focus = focusData?.focusContext;
    if (focus?.subjectActorId) {
      setLocalFocus({
        subjectActorId: focus.subjectActorId,
        subjectDisplayName: focus.subjectDisplayName ?? focus.subjectActorId,
        perspective: focus.perspective === 'performer' ? 'performer' : 'subject',
      });
    }
  }, [focusData]);

  const [setFocusMutation] = useSetFocusContextMutation();
  const [clearFocusMutation] = useClearFocusContextMutation();

  const setFocus = useCallback(
    (actorId: string, displayName?: string, perspective: 'subject' | 'performer' = 'subject') => {
      // Optimistic update
      setLocalFocus({
        subjectActorId: actorId,
        subjectDisplayName: displayName ?? actorId,
        perspective,
      });
      // Fire mutation (don't await — optimistic)
      setFocusMutation({
        variables: { subjectActorId: actorId, perspective },
      }).catch((e) => {
        // Revert on failure
        console.warn('Failed to set focus context:', e);
        setLocalFocus({ subjectActorId: null, subjectDisplayName: null, perspective: 'subject' });
      });
    },
    [setFocusMutation],
  );

  const clearFocus = useCallback(() => {
    setLocalFocus({ subjectActorId: null, subjectDisplayName: null, perspective: 'subject' });
    clearFocusMutation().catch((e) => {
      console.warn('Failed to clear focus context:', e);
    });
  }, [clearFocusMutation]);

  const value = useMemo<FocusContextValue>(
    () => ({
      focusedSubject: localFocus.subjectActorId,
      focusedDisplayName: localFocus.subjectDisplayName,
      perspective: localFocus.perspective,
      isFocused: localFocus.subjectActorId !== null,
      setFocus,
      clearFocus,
      loading: queryLoading,
    }),
    [localFocus, setFocus, clearFocus, queryLoading],
  );

  return <FocusCtx.Provider value={value}>{children}</FocusCtx.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useFocusContext(): FocusContextValue {
  return useContext(FocusCtx);
}
